import React, { useEffect, useCallback, useState } from 'react';
import HealtheraTable from '../Table/Table';
import { Loading, MainTitle } from '../../healtheraComponents';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../redux/surgeries';
import { withRouter } from 'react-router';
import styled from 'styled-components';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import useLocationQuery from '../../hooks/useQuery';

const SurgeriesList = () => {
  const {
    surgeries,
    fetching,
    filter,
    rowsPerPage,
    totalCount,
    searchResults: {
      surgeries: searchSurgeries,
      rowsPerPage: searchRowsPerPage,
      totalCount: searchTotalCount,
      currentPage: searchPage = 0,
    } = {}
  } = useSelector((state) => state.surgeries);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const { changeLocationQuery, query } = useLocationQuery();

  useEffect(() => {
    dispatch(actions.getSurgeries(null, query.page, filter));
  }, [query, filter, dispatch])

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      if (e.target.value !== undefined) setSearchTerm(e.target.value);
      else {
        if (searchTerm.length > 2) {
          dispatch(actions.search(searchTerm));
        } else {
          dispatch(actions.resetSearch());
        }
      }
    },
    [dispatch, searchTerm]
  );

  const resetSearch = useCallback(() => {
    dispatch(actions.resetSearch());
    setSearchTerm('');
  }, [dispatch]);

  const getData = useCallback(
    (...args) => dispatch(actions.getSurgeries(args[0], args[1], filter)),
    [dispatch, filter]
  );

  const resetPagination = (_, page) => {
    changeLocationQuery({
      ...query,
      page
    });
  };

  const handleSearchPagination = (_, page) => {
    dispatch(actions.search(searchTerm, {
      limit: searchRowsPerPage,
      page
    } ));
  }

  const setFilter = useCallback((type, value) => {
    let newFilter = {...filter, [type]: value};

    if (value === 'none') {
      let { [type]: deletedFilter, ...restFilters } = newFilter // delete filter
      newFilter = restFilters
    }

    dispatch(actions.setFilter(newFilter))
    dispatch(actions.getSurgeries(null, 0, newFilter))
  }, [dispatch, filter])

  return (
    <>
      <form onSubmit={handleSearch}>
        <SearchBox>
          <Search
            placeholder="Filter by Surgery Name, ODS Code, Postcode"
            value={searchTerm}
            onChange={handleSearch}
            submit
          />
          {searchSurgeries ? (
            <CloseIconStyled nativeColor={'#333'} onClick={resetSearch} />
          ) : (
            <SearchIconStyled nativeColor={'#333'} onClick={handleSearch} />
          )}
        </SearchBox>
      </form>
      {!searchSurgeries && !fetching && surgeries && (
        <HealtheraTable
          displayPagination
          getData={getData}
          onChangePage={resetPagination}
          page={+query.page || 0}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          data={prepareData(surgeries, { filter, setFilter })}
        />
      )}
      {!fetching && searchSurgeries && (
        <HealtheraTable
          displayPagination
          onChangePage={handleSearchPagination}
          data={prepareData(searchSurgeries, { filter, setFilter }, true)}
          page={searchPage}
          rowsPerPage={searchRowsPerPage}
          totalCount={searchTotalCount}
        />
      )}
      <Loading loading={fetching} />
    </>
  );
};
export default withRouter(SurgeriesList);

const prepareData = (surgeries = [], { filter, setFilter }, isSearch) => {
  return surgeries.reduce(
    (data, surgery = {}) => {
      const {
        surgery_name,
        _id,
        odsCode,
        branchCode,
        postcode,
        closed,
        integration_method,
        gpsoc,
      } = surgery;
      data.push({
        view: {
          name: surgery_name || '',
          odsCode: odsCode,
          branchCode: branchCode,
          postcode: postcode,
          state: closed ? 'Closed' : 'Open',
          integration: integration_method,
          gpsoc: gpsoc,
        },
        ID: _id,
        route: 'surgeries'
      });
      return data;
    },
    [
      {
        view: {
          name: 'Surgery Name',
          odsCode: 'ODS Code',
          branchCode: 'Branch Code',
          postcode: 'Postcode',
          state: isSearch ? (
            'State'
          ) : (
            <MainTitle>
              <Select
                value={filter.closed === undefined ? 'none' : filter.closed }
                onChange={(e) => setFilter('closed', e.target.value)}
                disableUnderline
              >
                <MenuItem value={'none'}>
                  <FilterLabel>State</FilterLabel>
                </MenuItem>
                <MenuItem value={false}>
                  <FilterLabel>Open</FilterLabel>
                </MenuItem>
                <MenuItem value={true}>
                  <FilterLabel>Closed</FilterLabel>
                </MenuItem>
              </Select>
            </MainTitle>
          ),
          integration: isSearch ? (
            'Integration Method'
          ) : (
            <MainTitle>
              <Select
                value={filter.integration_method || 'none'}
                onChange={(e) => setFilter('integration_method', e.target.value)}
                disableUnderline
              >
                <MenuItem value={'none'}>
                  <FilterLabel>Integration Method</FilterLabel>
                </MenuItem>
                <MenuItem value={'fax'}>
                  <FilterLabel>Fax</FilterLabel>
                </MenuItem>
                <MenuItem value={'email'}>
                  <FilterLabel>Email</FilterLabel>
                </MenuItem>
                <MenuItem value={'gpsoc'}>
                  <FilterLabel>GPSoC</FilterLabel>
                </MenuItem>
              </Select>
            </MainTitle>
          ),
          gpsoc: isSearch ? (
            'GPSOC status'
          ) : (
            <MainTitle>
              <Select
                value={filter.gpsoc || 'none'}
                onChange={(e) => setFilter('gpsoc', e.target.value)}
                disableUnderline
              >
                <MenuItem value={'none'}>
                  <FilterLabel>GPSOC status</FilterLabel>
                </MenuItem>
                <MenuItem value={'emis'}>
                  <FilterLabel>EMIS</FilterLabel>
                </MenuItem>
                <MenuItem value={'tpp'}>
                  <FilterLabel>TPP</FilterLabel>
                </MenuItem>
              </Select>
            </MainTitle>
          ),
        },
        headers: true
      }
    ]
  );
};

const FilterLabel = styled.span`
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.75rem;
  font-weight: 500;
`;

const Search = styled.input`
  && {
    height: 43px;
    border-radius: 13px;
    background-color: #ffffff;
    font-size: 16px;
    padding: 0 12px;
    color: #333;
    width: 475px;
    display: inline-block;
  }
  border: 0;
  &&:focus {
    outline: none;
  }
`;
const SearchBox = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  margin: 12px;
  height: 43px;
  border-radius: 13px;
  box-shadow: 0 1px 2px 0 rgba(80, 80, 80, 0.2);
  background-color: #ffffff;
  width: 500px;
`;

const SearchIconStyled = styled(SearchIcon)`
  width: 25px;
  margin-right: 15px;
  cursor: pointer;
`;
const CloseIconStyled = styled(CloseIcon)`
  width: 25px;
  margin-right: 15px;
  cursor: pointer;
`;
